import React from 'react';
import { FormattedMessage } from 'react-intl';
import { textColorOn } from '../../utils/themeContrast';

/**
 * The survey thank-you page, replicated for previews.
 *
 * ONE copy, shared by the concierge offer screen and the survey wizard. Both
 * previously drew their own approximation and both were wrong in different
 * ways — the wizard had no check circle and square primary-coloured buttons,
 * the offer screen was roughly half scale.
 *
 * Every number was read off the live page with getComputedStyle rather than
 * eyeballed. If brand-survey-app's Survey.tsx completion screen changes, this
 * has to move with it; nothing enforces that automatically because the two
 * live in different repos with different styling systems.
 */
export interface ThankYouTheme {
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
  cardColor?: string;
  accentColor?: string;
}


/**
 * Opens a link typed into this screen.
 *
 * These URLs have not been near the server yet, so the scheme check happens
 * here: a pasted `javascript:` value opened via window.open would execute in
 * this origin. Mirrors the survey app's own openExternalLink guard.
 */
const openIfSafe = (raw: string): boolean => {
  const value = raw?.trim();
  if (!value) return false;
  let url: URL;
  try {
    url = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
  } catch {
    return false;
  }
  if (!/^https?:$/i.test(url.protocol)) return false;
  window.open(url.toString(), '_blank', 'noopener,noreferrer');
  return true;
};

/**
 * The survey thank-you page, replicated.
 *
 * Every number here was read off the live page with getComputedStyle rather
 * than eyeballed, because an approximation is worse than useless for a screen
 * whose whole job is "this is what your customer sees": 448px card, 24px
 * radius, 32px padding, 96px check circle around a 64px glyph, 30px headline,
 * 20px subline, 16px-radius code box at 20px padding, 24px code at 2.4px
 * tracking, pill buttons at 12px/24px. Keep them in step with Survey.tsx.
 */
export const ThankYouPreview: React.FC<{
  headline: string;
  subtitle: string | null;
  theme?: ThankYouTheme | null;
  code: string;
  offer: string;
  terms: string;
  showDestination: boolean;
  showReview: boolean;
  destinationLabel: string;
  reviewLabel: string;
  // Optional: supplied by the concierge offer screen so its buttons really
  // open the links. The wizard renders the same page without them.
  destinationUrl?: string;
  reviewUrl?: string;
  onBadLink?: () => void;
}> = ({
  headline,
  subtitle,
  theme,
  code,
  offer,
  terms,
  showDestination,
  showReview,
  destinationLabel,
  reviewLabel,
  destinationUrl,
  reviewUrl,
  onBadLink,
}) => {
  const interactive = Boolean(destinationUrl !== undefined || reviewUrl !== undefined);
  const [copied, setCopied] = React.useState(false);

  // Same behaviour and fallback as the survey app, so what they try here is
  // what their customer gets.
  const handleCopy = async () => {
    if (!code.trim()) return;
    let done = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code.trim());
        done = true;
      }
    } catch {
      done = false;
    }
    if (!done) {
      try {
        const scratch = document.createElement('textarea');
        scratch.value = code.trim();
        scratch.setAttribute('readonly', '');
        scratch.style.position = 'absolute';
        scratch.style.left = '-9999px';
        document.body.appendChild(scratch);
        scratch.select();
        document.execCommand('copy');
        document.body.removeChild(scratch);
        done = true;
      } catch {
        done = false;
      }
    }
    if (done) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };
  // Same fallbacks the survey app uses (Survey.tsx), so an unthemed survey
  // previews as it will actually render rather than as a different dark box.
  const background = theme?.backgroundColor || '#312e81';
  const card = theme?.cardColor || '#3730a3';
  const primary = theme?.primaryColor || '#4338ca';
  const secondary = theme?.secondaryColor || '#2563eb';
  const accent = theme?.accentColor || '#F59E0B';
  const cardText = textColorOn(card);
  const secondaryText = textColorOn(secondary);

  return (
    <div
      style={{
        background,
        borderRadius: 12,
        padding: 24,
        display: 'flex',
        justifyContent: 'center',
        // The real page is full-viewport; a scrollable window keeps the card at
        // true size instead of shrinking it to fit.
        overflowX: 'auto',
      }}
    >
      <div
        style={{
          width: 448,
          flex: '0 0 448px',
          // Tailwind's preflight makes the real card border-box, so its 448px
          // is the OUTER width. Without this the padding is added on top and
          // the preview card comes out 514px wide.
          boxSizing: 'border-box',
          background: card,
          border: `1px solid ${primary}`,
          borderRadius: 24,
          padding: 32,
          textAlign: 'center',
          color: cardText,
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.25)',
        }}
      >
        {/* The same stroked check the survey app draws (Survey.tsx): identical
            path, 64px inside a 96px circle filled with the theme's secondary. */}
        <div
          style={{
            width: 96,
            height: 96,
            borderRadius: 9999,
            background: secondary,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 24,
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={64}
            height={64}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            style={{ color: secondaryText }}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>

        <div style={{ fontSize: 30, fontWeight: 700, marginBottom: 16, lineHeight: 1.2 }}>
          {headline}
        </div>
        {subtitle && (
          <div style={{ fontSize: 20, opacity: 0.9, marginBottom: 24, lineHeight: 1.4 }}>
            {subtitle}
          </div>
        )}

        {code.trim() && (
          <div
            style={{
              border: `2px dashed ${accent}`,
              borderRadius: 16,
              padding: 20,
              marginBottom: 24,
            }}
          >
            {offer.trim() && (
              <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>{offer}</div>
            )}
            <div
              style={{
                fontSize: 24,
                fontWeight: 700,
                letterSpacing: '2.4px',
                color: accent,
                marginBottom: 12,
              }}
            >
              {code}
            </div>
            <button
              type="button"
              onClick={handleCopy}
              style={{
                border: `1px solid ${accent}`,
                color: copied ? textColorOn(accent) : accent,
                background: copied ? accent : 'transparent',
                borderRadius: 9999,
                padding: '8px 16px',
                fontSize: 14,
                fontWeight: 500,
                display: 'inline-block',
                cursor: interactive ? 'pointer' : 'default',
                fontFamily: 'inherit',
              }}
            >
              <FormattedMessage
                id={copied ? 'offer.preview.copied' : 'offer.preview.copyCode'}
              />
            </button>
            {terms.trim() && (
              <div style={{ fontSize: 14, opacity: 0.75, marginTop: 12 }}>{terms}</div>
            )}
          </div>
        )}

        {/* No placeholder when both are off: the customer would see nothing
            there, and inventing filler would make the preview lie. */}
        {(showDestination || showReview) && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
          }}
        >
          {showDestination && (
            <button
              type="button"
              onClick={() => {
                if (!interactive) return;
                if (!openIfSafe(destinationUrl ?? '')) onBadLink?.();
              }}
              style={{
                background: secondary,
                color: secondaryText,
                border: 'none',
                borderRadius: 9999,
                padding: '12px 24px',
                fontWeight: 500,
                fontSize: 16,
                cursor: interactive ? 'pointer' : 'default',
                fontFamily: 'inherit',
              }}
            >
              {destinationLabel}
            </button>
          )}
          {showReview && (
            <button
              type="button"
              onClick={() => {
                if (!interactive) return;
                if (!openIfSafe(reviewUrl ?? '')) onBadLink?.();
              }}
              style={{
                border: `2px solid ${secondary}`,
                background: 'transparent',
                color: cardText,
                borderRadius: 9999,
                padding: '12px 24px',
                fontWeight: 500,
                fontSize: 16,
                cursor: interactive ? 'pointer' : 'default',
                fontFamily: 'inherit',
              }}
            >
              {reviewLabel}
            </button>
          )}
        </div>
        )}
      </div>
    </div>
  );
};


export default ThankYouPreview;
